import {getTestProps} from '@benchling/js-test-utils';
import {Color} from '@benchling/nucleus-ui';
import {
  Box,
  Button,
  CloseButton,
  Collapse,
  createStyles,
  CSSObject,
  Divider,
  Group,
  Paper,
  Stack,
  Text,
  Transition,
  UnstyledButton,
} from '@mantine/core';
import {useClickOutside, useMediaQuery} from '@mantine/hooks';
import React, {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {LLM_CHAT_ANALYTICS_ACTIONS} from 'constants/analytics-products/LLMChatConstants';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {
  getIntroProgressiveDisclosureCategories,
  ProgressiveDisclosureCategory,
} from './ProgressiveDisclosureData';

interface ProgressiveDisclosureProps {
  isStacked?: boolean;
  isVisible?: boolean;
  /** Skip the exit animation when the containing intro is hard-swapping layouts. */
  hideImmediately?: boolean;
  onExitComplete?: () => void;
  preserveHiddenGeometry?: boolean;
  onHoverOption: (prompt: string | null) => void;
  onSelectOption: (prompt: string) => void;
}

const DISCLOSURE_ITEM_EXIT_MS = 40;
const DISCLOSURE_ITEM_EXIT_STAGGER_MS = 15;
/** @visibleForTesting */
export const DISCLOSURE_ITEM_ENTER_MS = 420;
/** @visibleForTesting */
export const DISCLOSURE_ITEM_ENTER_STAGGER_MS = 70;
const DISCLOSURE_ITEM_ENTER_OFFSET_PX = 14;
const DISCLOSURE_HEIGHT_TRANSITION_MS = 180;
export const PROGRESSIVE_DISCLOSURE_EXIT_TRANSITION_MS = 100;
export const PROGRESSIVE_DISCLOSURE_ENTER_TRANSITION_MS = 220;

function preserveExitedTransitionGeometry(transitionStyles: React.CSSProperties): React.CSSProperties {
  return transitionStyles.display === 'none'
    ? {...transitionStyles, display: 'block', opacity: 0, visibility: 'hidden'}
    : transitionStyles;
}

/** @visibleForTesting */
export const DISCLOSURE_ICON_SIZE_PX = 14;

/**
 * Font Awesome sizes its glyphs from each icon's own aspect ratio, so pin an explicit square box to keep
 * every category glyph identical regardless of which icon a category uses. The doubled selector outranks
 * the Font Awesome stylesheet's own width rule.
 */
const squareIconStyles: CSSObject = {
  '&&': {
    width: DISCLOSURE_ICON_SIZE_PX,
    height: DISCLOSURE_ICON_SIZE_PX,
    fontSize: DISCLOSURE_ICON_SIZE_PX,
    flexShrink: 0,
  },
};

const useStyles = createStyles((theme, {isStacked}: {isStacked: boolean}) => ({
  container: {
    paddingTop: isStacked ? theme.spacing.lg : theme.spacing.md,
    paddingRight: isStacked ? 0 : theme.spacing.md,
    paddingBottom: isStacked ? theme.spacing.sm : theme.spacing.md,
    paddingLeft: isStacked ? 0 : theme.spacing.md,
  },
  optionItem: {
    display: 'block',
    width: '100%',
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    borderRadius: 0,
    fontSize: theme.fontSizes.md,
    color: Color.GRAY_900,
    textAlign: 'left',
    '&:hover': {
      backgroundColor: theme.colors.blue[0],
    },
  },
  optionsPanel: {
    borderRadius: theme.radius.lg,
    overflow: 'hidden',
    padding: theme.spacing.sm,
  },
  optionsPanelTitle: {
    color: Color.GRAY_500,
  },
  optionsPanelTitleIcon: {
    ...squareIconStyles,
    color: Color.GRAY_500,
    '& svg': {
      color: Color.GRAY_500,
    },
  },
  divider: {
    borderColor: theme.colors.gray[2],
  },
  categoryPill: {
    fontWeight: theme.other.fontWeights.normal,
    backgroundColor: theme.white,
    color: Color.GRAY_900,
    border: `1px solid ${Color.GRAY_250}`,
    '&:hover': {
      backgroundColor: Color.BLUE_100,
      borderColor: Color.BLUE_100,
      color: Color.BLUE_450,
    },
    '&:hover $categoryPillLeftIcon': {
      opacity: 1,
    },
  },
  categoryPillLeftIcon: {
    marginRight: theme.spacing.sm,
    opacity: 0.5,
  },
  categoryPillIcon: squareIconStyles,
}));

export default function ProgressiveDisclosure({
  isStacked = false,
  isVisible = true,
  hideImmediately = false,
  onExitComplete,
  preserveHiddenGeometry = false,
  onHoverOption,
  onSelectOption,
}: ProgressiveDisclosureProps): JSX.Element {
  const {classes} = useStyles({isStacked});
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);
  const previousIsStackedRef = useRef(isStacked);
  const lastHoveredOptionRef = useRef<string | null>(null);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const categoryListRef = useRef<HTMLDivElement>(null);
  const [categoryListMinHeight, setCategoryListMinHeight] = useState(0);
  const [isHeightCollapsed, setIsHeightCollapsed] = useState(!isVisible && !preserveHiddenGeometry);
  const [hasCompletedExit, setHasCompletedExit] = useState(!isVisible);
  const [hasCompletedEnter, setHasCompletedEnter] = useState(isVisible);

  const categories = getIntroProgressiveDisclosureCategories();
  const activeCategory = categories.find((c) => c.id === activeCategoryId) ?? null;

  useLayoutEffect(() => {
    if (!isStacked || activeCategory != null || categoryListRef.current == null) {
      return;
    }
    const measuredHeight = categoryListRef.current.getBoundingClientRect().height;
    if (measuredHeight > 0) {
      setCategoryListMinHeight(measuredHeight);
    }
  }, [activeCategory, categories.length, isStacked]);

  useEffect(() => {
    if (isVisible) {
      setHasCompletedExit(false);
      setIsHeightCollapsed(false);
      return;
    }
    if (preserveHiddenGeometry) {
      setIsHeightCollapsed(false);
    } else if (hasCompletedExit) {
      setIsHeightCollapsed(true);
    }
  }, [hasCompletedExit, isVisible, preserveHiddenGeometry]);

  const clearHoverTimer = useCallback(() => {
    if (hoverTimerRef.current != null) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  }, []);

  useEffect(() => clearHoverTimer, [clearHoverTimer]);

  const dismissPanel = useCallback(() => {
    clearHoverTimer();
    setActiveCategoryId(null);
    onHoverOption(null);
    lastHoveredOptionRef.current = null;
  }, [onHoverOption, clearHoverTimer]);

  useEffect(() => {
    if (previousIsStackedRef.current !== isStacked) {
      previousIsStackedRef.current = isStacked;
      if (activeCategoryId != null) {
        dismissPanel();
      }
    }
  }, [activeCategoryId, dismissPanel, isStacked]);

  const containerRef = useClickOutside<HTMLDivElement>(dismissPanel);

  const handleCategoryClick = useCallback((category: ProgressiveDisclosureCategory) => {
    setActiveCategoryId(category.id);
    lastHoveredOptionRef.current = null;
    trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.CLICKED_PROGRESSIVE_DISCLOSURE_CATEGORY, {
      categoryId: category.id,
    });
  }, []);

  const handleOptionClick = useCallback(
    (categoryId: string, optionId: string, prompt: string) => {
      clearHoverTimer();
      trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.SUBMITTED_PROGRESSIVE_DISCLOSURE_OPTION, {
        categoryId,
        optionId,
      });
      onHoverOption(null);
      setActiveCategoryId(null);
      onSelectOption(prompt);
    },
    [onHoverOption, onSelectOption, clearHoverTimer]
  );

  const handleExitComplete = useCallback(() => {
    setHasCompletedExit(true);
    if (!preserveHiddenGeometry) {
      setIsHeightCollapsed(true);
    }
    onExitComplete?.();
  }, [onExitComplete, preserveHiddenGeometry]);

  const handleEnterStart = useCallback(() => {
    setHasCompletedEnter(false);
  }, []);

  const handleEnterComplete = useCallback(() => {
    setHasCompletedEnter(true);
  }, []);

  const handleExitStart = useCallback(() => {
    setHasCompletedEnter(false);
  }, []);

  const renderCategoryButton = (category: ProgressiveDisclosureCategory): JSX.Element => (
    <Button
      key={category.id}
      variant="ghost"
      radius="xl"
      size="sm"
      classNames={{root: classes.categoryPill, leftIcon: classes.categoryPillLeftIcon}}
      leftIcon={
        <BenchlingIcon icon={category.icon} aria-hidden={true} className={classes.categoryPillIcon} />
      }
      onClick={() => {
        handleCategoryClick(category);
      }}
    >
      {category.label}
    </Button>
  );

  const categoryButtons = categories.map((category, index) => {
    const transitionDurationMs = prefersReducedMotion
      ? 0
      : isVisible
        ? DISCLOSURE_ITEM_ENTER_MS
        : hideImmediately
          ? 0
          : DISCLOSURE_ITEM_EXIT_MS;
    const transitionDelayMs =
      (prefersReducedMotion
        ? 0
        : isVisible
          ? DISCLOSURE_ITEM_ENTER_STAGGER_MS
          : hideImmediately
            ? 0
            : DISCLOSURE_ITEM_EXIT_STAGGER_MS) * index;
    const isLastCategory = index === categories.length - 1;
    return (
      <Transition
        key={category.id}
        keepMounted={true}
        mounted={isVisible}
        transition={{
          in: {opacity: 1, transform: 'translateY(0)'},
          out: {opacity: 0, transform: `translateY(${DISCLOSURE_ITEM_ENTER_OFFSET_PX}px)`},
          transitionProperty: 'opacity, transform',
        }}
        duration={transitionDurationMs + transitionDelayMs}
        exitDuration={transitionDurationMs + transitionDelayMs}
        timingFunction="ease"
        onEnter={isLastCategory ? handleEnterStart : undefined}
        onEntered={isLastCategory ? handleEnterComplete : undefined}
        onExit={isLastCategory ? handleExitStart : undefined}
        onExited={isLastCategory ? handleExitComplete : undefined}
      >
        {(transitionStyles) => (
          <div
            style={{
              ...preserveExitedTransitionGeometry(transitionStyles),
              ...(transitionStyles.display === 'none'
                ? {transform: `translateY(${DISCLOSURE_ITEM_ENTER_OFFSET_PX}px)`}
                : {}),
              transitionDelay: `${transitionDelayMs}ms`,
              transitionDuration: `${transitionDurationMs}ms`,
            }}
            {...getTestProps({
              component: 'ProgressiveDisclosure',
              element: 'category',
              key: category.id,
            })}
          >
            {renderCategoryButton(category)}
          </div>
        )}
      </Transition>
    );
  });

  return (
    <Collapse
      in={!isHeightCollapsed}
      animateOpacity={false}
      transitionDuration={prefersReducedMotion ? 0 : DISCLOSURE_HEIGHT_TRANSITION_MS}
      data-transition-complete={hasCompletedEnter}
      {...getTestProps({component: 'ProgressiveDisclosure', element: 'collapse'})}
    >
      <div ref={activeCategoryId != null ? containerRef : undefined} className={classes.container}>
        <Box
          style={{
            minHeight: isStacked && categoryListMinHeight > 0 ? categoryListMinHeight : undefined,
          }}
        >
          {activeCategory == null && (
            <>
              {isStacked ? (
                <Stack ref={categoryListRef} spacing="sm" align="flex-start">
                  {categoryButtons}
                </Stack>
              ) : (
                <Group spacing="sm" position="center">
                  {categoryButtons}
                </Group>
              )}
            </>
          )}
          {activeCategory != null && (
            <Transition
              keepMounted={true}
              mounted={isVisible}
              transition="fade"
              duration={prefersReducedMotion ? 0 : PROGRESSIVE_DISCLOSURE_ENTER_TRANSITION_MS}
              exitDuration={prefersReducedMotion ? 0 : PROGRESSIVE_DISCLOSURE_EXIT_TRANSITION_MS}
              onEnter={handleEnterStart}
              onEntered={handleEnterComplete}
              onExit={handleExitStart}
              onExited={handleExitComplete}
            >
              {(transitionStyles) => (
                <Paper
                  withBorder={true}
                  className={classes.optionsPanel}
                  style={preserveExitedTransitionGeometry(transitionStyles)}
                >
                  <Group position="apart" px="md" pt="sm" pb={4}>
                    <Group spacing="sm">
                      <BenchlingIcon
                        icon={activeCategory.icon}
                        aria-hidden={true}
                        className={classes.optionsPanelTitleIcon}
                      />
                      <Text size="sm" className={classes.optionsPanelTitle}>
                        {activeCategory.label}
                      </Text>
                    </Group>
                    <CloseButton size="sm" onClick={dismissPanel} aria-label="Close suggestions" />
                  </Group>
                  <Stack spacing={0} pb={0}>
                    {activeCategory.options.map((option, index) => (
                      <React.Fragment key={option.id}>
                        {index > 0 && <Divider className={classes.divider} />}
                        <UnstyledButton
                          className={classes.optionItem}
                          onMouseEnter={() => {
                            const key = `${activeCategory.id}:${option.id}`;
                            hoverTimerRef.current = setTimeout(() => {
                              if (lastHoveredOptionRef.current !== key) {
                                lastHoveredOptionRef.current = key;
                                trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.HOVERED_PROGRESSIVE_DISCLOSURE_OPTION, {
                                  categoryId: activeCategory.id,
                                  optionId: option.id,
                                });
                              }
                            }, 400);
                            onHoverOption(option.prompt);
                          }}
                          onMouseLeave={() => {
                            clearHoverTimer();
                            onHoverOption(null);
                          }}
                          onClick={() => {
                            handleOptionClick(activeCategory.id, option.id, option.prompt);
                          }}
                        >
                          {option.label}
                        </UnstyledButton>
                      </React.Fragment>
                    ))}
                  </Stack>
                </Paper>
              )}
            </Transition>
          )}
        </Box>
      </div>
    </Collapse>
  );
}
